/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import { components as reactSelectComponents } from "react-select";
import styled, { keyframes } from "styled-components";
import i18n from "#/i18n";
import {
  Button, Modal, Select, Spinner, TextField,
} from "@xfrontend/xui-react-plus";
import {
  Edit, Delete, Save, Visible, Invisible, Defend, ErrorFill, LatestVersion, Start,
} from "@xfrontend/xicon";
import { RootState } from "#/types/RootState";
import {
  POLICY_DISPLAY_NAMES, POLICY_RISK_LEVEL, sortByRiskLevelDesc,
} from "#/types/xecguard";
import { getPolicies } from "#/modules/policies/slice";
import PanelProvider from "#/modules/common/PanelProvider";
import PolicySelectorModal, { PolicyOption } from "#/modules/common/PolicySelectorModal";
import RiskLevelTag from "#/modules/common/RiskLevelTag";
import {
  PROMPT_TEMPLATE_GROUPS, CATEGORY_POLICY_MAP, PromptTemplate, CustomPromptTemplate, mergeTemplateGroups,
} from "#/modules/sandbox/promptTemplates";
import useCustomTemplates from "#/modules/sandbox/useCustomTemplates";
import TemplateFormModal, { TemplateFormValues } from "#/modules/sandbox/TemplateFormModal";
import postScan, { ScanMessage, ViolationResult } from "#/apis/infra/postScan";
import getServiceTokensApi, { ServiceTokenType } from "#/apis/infra/getServiceTokens";
import { formatTimestamp } from "#/utils/analytics";
import getTraceDetail, { TraceDetailType, TraceViolationItem } from "#/apis/infra/getTraceDetail";
import ReactScrollbarsCustom from "#/modules/common/ReactScrollbarsCustom";
import * as infraActions from "#/infra/redux/actionType";

const AttackScenarioMenuList = (props: any) => (
  <reactSelectComponents.MenuList {...props}>
    <div style={{ maxHeight: "560px", overflowY: "auto" }}>
      {props.children}
    </div>
  </reactSelectComponents.MenuList>
);

const formatErrorTimestamp = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

const parseScanErrorMessage = (message?: string) => {
  if (!message) return i18n.t("sandbox:requestFailed");
  try {
    const parsed = JSON.parse(message);
    return parsed?.error?.message ?? parsed?.message ?? message;
  } catch {
    return message;
  }
};

const isValidServiceToken = (token: string) => token.startsWith("xgs_") && token.length === 53;

const delay = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const PageWrapper = styled.div`
  padding: 12px 12px 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
`;

const PageTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
  white-space: nowrap;
`;

const TokenGroup = styled.div`
  display: grid;
  grid-template-columns: auto 240px auto;
  column-gap: 8px;
  align-items: center;
`;

const TokenLabel = styled.span`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  white-space: nowrap;
`;

const TokenInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 240px;
`;

const TokenInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 36px;
  padding: 0 36px 0 12px;
  border-radius: 4px;
  border: 1px solid ${(props: { hasError?: boolean }) => (props.hasError ? "var(--x-red, #ff595a)" : "var(--x-purple-500, #494358)")};
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  box-sizing: border-box;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }

  &:focus {
    outline: none;
    border-color: ${(props: { hasError?: boolean }) => (props.hasError ? "var(--x-red, #ff595a)" : "var(--x-red, #ff595a)")};
    box-shadow: none;
  }
`;

const TokenToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--x-gray-400, #cfd0d3);

  &:hover {
    color: var(--x-gray-200, #f6f6f7);
  }
`;

const TokenErrorText = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: var(--x-red, #ff595a);
  white-space: nowrap;
`;

const TokenInfoText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-400, #cfd0d3);
  white-space: nowrap;
`;

const PolicyErrorText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--x-red, #ff595a);
`;

const PanelsRow = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

const LeftPanel = styled.div`
  background: var(--basicWrap, #342f40);
  border-radius: 8px;
  padding: 24px 24px 12px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const AnalysisPanel = styled.div`
  background: var(--basicWrap, #342f40);
  border-radius: 8px;
  width: 400px;
  min-width: 400px;
  min-height: 0;
  overflow: hidden;
`;

const AnalysisPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const TestResultBanner = styled.div<{ variant?: "safe" | "unsafe" }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
  padding: var(--spacing-xs, 6px) var(--spacing-md, 12px);
  border-radius: var(--border-radius, 4px);
  background: ${(props: { variant?: "safe" | "unsafe" }) => (props.variant === "safe" ? "var(--x-cyan-600, #00a1af)" : "var(--x-red, #ff595a)")};
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
`;

const AnalysisHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const AnalysisIcon = styled.span`
  font-size: 18px;
  color: var(--x-gray-200, #f6f6f7);
`;

const AnalysisTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  background: var(--x-purple-700, #2c2835);
  border-radius: 4px;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: 20px;
  font-weight: 700;
  color: ${(props: { color?: string }) => props.color || "var(--x-gray-200, #f6f6f7)"};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--x-gray-400, #cfd0d3);
`;

const FieldRow = styled.div`
  margin-bottom: 12px;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--x-gray-500, #a9a9ac);
  margin-bottom: 2px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: var(--x-gray-200, #f6f6f7);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--x-purple-500, #494358);
  margin: 8px 0 16px;
`;

const ViolationsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 8px;
`;

const ViolationsEmpty = styled.div`
  font-size: 13px;
  color: var(--x-gray-400, #cfd0d3);
`;

const ViolationCard = styled.div`
  background: var(--x-bg-400, #3b3649);
  border: 1px solid var(--x-red, #ff595a);
  border-radius: var(--border-radius, 4px);
  padding: var(--spacing-md, 12px);
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ViolationHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViolationPolicyName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
`;

const ViolationFieldLabel = styled.div`
  font-size: 13px;
  color: var(--x-gray-500, #a9a9ac);
`;

const ViolationFieldValue = styled.div`
  font-size: 13px;
  color: var(--x-gray-200, #f6f6f7);
  line-height: 1.5;
`;

const EvidenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EvidenceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;
`;

const EvidenceTypeBadge = styled.span`
  background: rgba(255, 89, 90, 0.2);
  color: var(--x-red, #ff595a);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  flex-shrink: 0;
`;

const EvidenceText = styled.span`
  background: var(--x-yellow, #f2c94c);
  color: var(--x-gray-900, #1a1726);
  font-family: "Roboto Mono", monospace;
  font-weight: 600;
  border-radius: 2px;
  padding: 0 4px;
  overflow-wrap: anywhere;
`;

const EvidencePending = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--x-gray-400, #cfd0d3);
  font-style: italic;
`;

const EvidenceUnavailable = styled.div`
  font-size: 12px;
  color: var(--x-gray-500, #a9a9ac);
  font-style: italic;
`;

const spin360 = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinIcon = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1.5px solid var(--x-gray-500, #a9a9ac);
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin360} 0.9s linear infinite;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--x-red, #ff595a);
  border-radius: 4px;
  background: rgba(255, 89, 90, 0.08);
`;

const ErrorIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const ErrorContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorMessage = styled.div`
  font-size: 13px;
  color: var(--x-gray-200, #f6f6f7);
  line-height: 1.5;
  word-break: break-word;
`;

const ErrorTimestamp = styled.div`
  font-size: 12px;
  color: var(--x-gray-400, #cfd0d3);
  line-height: 1.5;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const HeaderTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  flex: 1;
  min-height: 0;
`;

const LeftColumn = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const LeftColumnInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RightColumn = styled.div`
  background: var(--x-dark-dark, #191423);
  border-radius: 8px;
  overflow: hidden;
`;

const RightColumnInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--x-gray-400, #cfd0d3);
`;

const PoliciesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PoliciesTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--x-gray-400, #cfd0d3);
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--x-purple-500, #494358);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 12px;
  font-weight: 700;
`;

const PoliciesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PolicyRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
`;

const PolicyTagWrap = styled.div`
  width: 65px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
`;

const PolicyName = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const SelectPoliciesButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 13px;
  cursor: pointer;
  width: fit-content;

  &:hover {
    background: var(--x-purple-500, #494358);
  }
`;

const MessageBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
`;

type RoleOption = { value: string; label: string };

const SaveTemplateLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 13px;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--x-gray-200, #f6f6f7);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TemplateCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TemplateCardActions = styled.div`
  display: flex;
  gap: 4px;
`;

const TemplateActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

const AddMessageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background: var(--x-theme-secondary, #73698A);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  cursor: pointer;
  width: fit-content;

  &:hover {
    opacity: 0.85;
  }
`;

const TemplateStyledModal = styled(Modal)`
  .modal-dialog {
    width: 800px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border: 1px solid var(--x-purple-500, #494358);
  }
`;

const AttackTemplatesTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 16px;
`;

const TemplateCategoryTitle = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--x-gray-400, #cfd0d3);
  margin-bottom: 8px;
  margin-top: 16px;

  &:first-child {
    margin-top: 0;
  }
`;

const TemplateCard = styled.div`
  background: var(--x-purple-500, #494358);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const TemplateFieldLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--x-gray-400);
`;

const TemplateFieldValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--x-gray-200);
  line-height: 1.5;
`;

const ForceTriggerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ApplyTemplateBtn = styled.button`
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: none;
  background: var(--x-theme-secondary, #73698A);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 13px;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

type MessageItem = {
  id: number;
  role: "user" | "assistant";
  content: string;
};

const Sandbox = (): JSX.Element => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const { t } = useTranslation("sandbox");
  const roleOptions: RoleOption[] = [
    { value: "user", label: t("messages.userInput") },
    { value: "assistant", label: t("templateForm.roleAssistant") },
  ];
  const policiesData = useSelector(
    (state: RootState) => state.policies?.list?.data,
  ) ?? [];
  const licenseTimezone = useSelector(
    (state: RootState) => state.dashboard.license.results?.license_info?.timezone,
  );

  const allPolicyOptions: PolicyOption[] = useMemo(
    () => policiesData.map((p) => ({
      id: p.policy_id,
      policyName: p.policy_name,
      name: POLICY_DISPLAY_NAMES[p.policy_name] ?? p.policy_name,
      description: p.description ?? "",
      riskLevel: p.risk_level ?? 2,
      category: (p.policy_id.startsWith("default_") ? "default" : "custom") as "default" | "custom",
      updatedAt: formatTimestamp(p.updated_at, licenseTimezone) || "—",
    })),
    [policiesData, licenseTimezone],
  );

  const policyRiskLevelByName = useMemo(
    () => new Map(allPolicyOptions.map((policy) => [policy.policyName, policy.riskLevel])),
    [allPolicyOptions],
  );

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      dispatch(getPolicies({ accessToken: auth.user.access_token, period: 7 }));
    }
  }, [auth.isAuthenticated, auth.user?.access_token, dispatch]);

  const cachedServiceTokens: ServiceTokenType[] = useSelector(
    (state: RootState) => state.infra.serviceTokens.results ?? [],
  );

  const [serviceToken, setServiceToken] = useState(
    () => sessionStorage.getItem("sandbox_service_token") ?? "",
  );
  const [serviceTokensData, setServiceTokensData] = useState<ServiceTokenType[]>(cachedServiceTokens);
  const [isRefreshingServiceTokens, setIsRefreshingServiceTokens] = useState(false);
  const [tokenVisible, setTokenVisible] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [isPolicySelectorOpen, setIsPolicySelectorOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateFormState, setTemplateFormState] = useState<null | { mode: "saveCurrent" | "addNew" | "edit"; editId?: string }>(null);
  const {
    customTemplates, addTemplate, updateTemplate, deleteTemplate,
  } = useCustomTemplates();

  const mergedGroups = useMemo(
    () => mergeTemplateGroups(PROMPT_TEMPLATE_GROUPS, customTemplates),
    [customTemplates],
  );

  const customCategoryNames = useMemo(
    () => [...new Set(customTemplates.map((t) => t.category))],
    [customTemplates],
  );
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("All");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanGroups, setScanGroups] = useState<Array<{
    scanType: "input" | "response";
    traceId: string;
    latencyMs?: number;
    decision: "SAFE" | "UNSAFE";
    violations: ViolationResult[];
  }>>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanErrorAt, setScanErrorAt] = useState<string | null>(null);
  const [scanLoaded, setScanLoaded] = useState(false);
  const pollSessionRef = useRef(0);
  // @ts-ignore
  const forceTrigger = process.env.XEC_FORCE_TRIGGER === "true";

  const resetScanResult = () => {
    setScanGroups([]);
    setScanError(null);
    setScanErrorAt(null);
    setScanLoaded(false);
  };

  useEffect(() => {
    setServiceTokensData(cachedServiceTokens);
  }, [cachedServiceTokens]);

  const refreshServiceTokens = async () => {
    if (!auth.user?.access_token) return serviceTokensData;

    setIsRefreshingServiceTokens(true);
    try {
      const response = await getServiceTokensApi({ token: auth.user.access_token });
      const latestTokens = response.success && response.data
        ? (Array.isArray(response.data) ? response.data : [response.data])
        : [];

      setServiceTokensData(latestTokens);
      dispatch({ type: infraActions.FETCHED_SERVICE_TOKENS, data: latestTokens });
      return latestTokens;
    } catch {
      setServiceTokensData([]);
      dispatch({ type: infraActions.FETCHED_SERVICE_TOKENS, data: [] });
      return [];
    } finally {
      setIsRefreshingServiceTokens(false);
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      refreshServiceTokens();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.access_token]);

  useEffect(() => {
    const handleFocus = () => {
      if (auth.isAuthenticated && auth.user?.access_token) {
        refreshServiceTokens();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.access_token]);

  const selectedPolicies = useMemo(
    () => selectedPolicyIds
      .map((id) => allPolicyOptions.find((p) => p.id === id))
      .filter((p): p is PolicyOption => p !== undefined),
    [selectedPolicyIds, allPolicyOptions],
  );

  const [messages, setMessages] = useState<MessageItem[]>([
    { id: 1, role: "user", content: "" },
    { id: 2, role: "assistant", content: "" },
    { id: 3, role: "user", content: "" },
  ]);

  const addMessage = () => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", content: "" },
    ]);
  };

  const updateMessage = (id: number, field: keyof MessageItem, value: string) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const resolveTemplateContent = (template: PromptTemplate) => {
    const key = template.i18nKey;
    if (!key) return { systemPrompt: template.systemPrompt, messages: template.messages };
    const sp = t(`templates.${key}.systemPrompt`, template.systemPrompt);
    const msgs = template.messages.map((m, i) => ({
      ...m,
      content: t(`templates.${key}.msg${i}`, m.content),
    }));
    return { systemPrompt: sp, messages: msgs };
  };

  const applyTemplate = (template: PromptTemplate, category: string) => {
    const resolved = resolveTemplateContent(template);
    resetScanResult();
    setSystemPrompt(resolved.systemPrompt);
    setMessages(
      resolved.messages.map((m, i) => ({
        id: i + 1,
        role: m.role as "user" | "assistant",
        content: m.content,
      })),
    );

    // Auto-select policies: "Total Policies Select *" selects the first five default policies.
    if (category === "Total Policies Select *") {
      const defaultIds = allPolicyOptions
        .filter((p) => p.category === "default" && p.id !== "default_context_grounding_validation")
        .sort(sortByRiskLevelDesc)
        .slice(0, 5)
        .map((p) => p.id);
      setSelectedPolicyIds(defaultIds);
    } else {
      const policyName = CATEGORY_POLICY_MAP[category];
      if (policyName) {
        const matched = policiesData.find((p) => p.policy_name === policyName);
        if (matched) {
          setSelectedPolicyIds([matched.policy_id]);
        }
      }
    }

    setIsTemplateModalOpen(false);
  };

  const handleSaveTemplate = (values: TemplateFormValues) => {
    if (templateFormState?.mode === "edit" && templateFormState.editId) {
      updateTemplate(templateFormState.editId, values);
    } else {
      addTemplate(values);
    }
    setTemplateFormState(null);
  };

  const handleDeleteTemplate = (id: string) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (confirm(t("templateForm.deleteConfirm"))) {
      deleteTemplate(id);
    }
  };

  const getTemplateFormInitialValues = () => {
    if (!templateFormState) return undefined;
    if (templateFormState.mode === "saveCurrent") {
      return {
        systemPrompt,
        messages: messages
          .filter((m) => m.content.trim())
          .map((m) => ({ role: m.role, content: m.content })),
      };
    }
    if (templateFormState.mode === "edit" && templateFormState.editId) {
      return customTemplates.find((t) => t.id === templateFormState.editId);
    }
    return undefined;
  };

  const getTemplateFormTitle = () => {
    if (templateFormState?.mode === "edit") return t("templateForm.editTitle");
    if (templateFormState?.mode === "saveCurrent") return t("templateForm.saveCurrentTitle");
    return t("templateForm.newTitle");
  };

  const hasScenarioContent = systemPrompt.trim().length > 0 || messages.some((m) => m.content.trim().length > 0);
  const trimmedServiceToken = serviceToken.trim();
  const hasValidServiceToken = isValidServiceToken(trimmedServiceToken);
  const serviceTokenId = trimmedServiceToken.slice(0, 20);
  const matchedServiceToken = serviceTokensData.find((token) => token.service_token_id === serviceTokenId);
  const showServiceTokenFormatError = (trimmedServiceToken.length > 0 || hasScenarioContent) && !hasValidServiceToken;
  const showServiceTokenLookupError = !showServiceTokenFormatError
    && trimmedServiceToken.length > 0
    && hasValidServiceToken
    && !isRefreshingServiceTokens
    && !matchedServiceToken;
  const showPolicyError = hasScenarioContent && selectedPolicyIds.length === 0;

  useEffect(() => {
    if (hasValidServiceToken) {
      refreshServiceTokens();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTokenId, hasValidServiceToken]);

  const handleRunAnalysis = async () => {
    if (!hasValidServiceToken) return;

    const latestServiceTokens = await refreshServiceTokens();
    const latestMatchedServiceToken = latestServiceTokens.find((token) => token.service_token_id === serviceTokenId);
    if (!latestMatchedServiceToken) return;

    // Build messages array
    const apiMessages: ScanMessage[] = [];
    if (systemPrompt.trim()) {
      apiMessages.push({ role: "system", content: systemPrompt });
    }
    const nonEmptyMsgs = messages.filter((m) => m.content.trim());
    if (nonEmptyMsgs.length === 0) return;
    const dummySuffix = "\n[[XECGUARD_DUMMY:cycraft_simulate:all]]";
    nonEmptyMsgs.forEach((m) => {
      const content = forceTrigger ? m.content + dummySuffix : m.content;
      apiMessages.push({ role: m.role, content });
    });

    // Determine which scan_types to run based on message roles
    const hasUser = nonEmptyMsgs.some((m) => m.role === "user");
    const hasAssistant = nonEmptyMsgs.some((m) => m.role === "assistant");
    const scanTypes: Array<"input" | "response"> = [];
    if (hasUser) scanTypes.push("input");
    if (hasAssistant) scanTypes.push("response");

    // Map selected policy IDs to policy_name (empty array = use token's default policies)
    const policyNames = selectedPolicyIds
      .map((id) => policiesData.find((p) => p.policy_id === id)?.policy_name)
      .filter((n): n is string => !!n);

    setScanLoading(true);
    setScanError(null);
    setScanGroups([]);
    setScanLoaded(false);

    /* eslint-disable @typescript-eslint/naming-convention */
    const runScan = async (scanType: "input" | "response") => {
      const result = await postScan({
        serviceToken: serviceToken.trim(),
        scan_type: scanType,
        messages: apiMessages,
        policy_names: policyNames,
      });
      return { scanType, result };
    };

    // Run scans sequentially: input first, then response
    const scanResults = await scanTypes.reduce(
      async (accPromise, st) => {
        const acc = await accPromise;
        const res = await runScan(st);
        return [...acc, res];
      },
      Promise.resolve([]) as Promise<Array<{ scanType: "input" | "response"; result: Awaited<ReturnType<typeof runScan>>["result"] }>>,
    );

    const groups: typeof scanGroups = [];
    let hasError = false;

    scanResults.forEach(({ scanType, result }) => {
      if (result.success && result.data) {
        groups.push({
          scanType,
          traceId: result.data.trace_id,
          decision: result.data.decision,
          violations: result.data.xecguard_result ?? [],
        });
      } else {
        setScanError(parseScanErrorMessage(result.msg));
        setScanErrorAt(formatErrorTimestamp());
        hasError = true;
      }
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    if (!hasError) {
      setScanGroups(groups);
      setScanLoaded(true);
      pollSessionRef.current += 1;
      const sessionId = pollSessionRef.current;

      const fetchAllTraces = async () => Promise.all(
        groups.map(async (g) => {
          if (!g.traceId) return null;
          const r = await getTraceDetail({
            traceId: g.traceId,
            token: auth.user?.access_token ?? "",
          });
          return r.success && r.data ? (r.data as TraceDetailType) : null;
        }),
      );

      const mergeTraces = (traces: Array<TraceDetailType | null>) => {
        setScanGroups((prev) => prev.map((g, i) => {
          const trace = traces[i];
          if (!trace) return g;
          const reasons: TraceViolationItem[] = trace.scan_result?.reasons ?? [];
          const newViolations = g.violations.map((v, vIdx) => {
            const traceV = reasons[vIdx];
            if (!traceV) return v;
            return {
              ...v,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              evidence_status: traceV.evidence_status ?? v.evidence_status,
              evidence: traceV.evidence ?? v.evidence,
            };
          });
          return {
            ...g,
            latencyMs: g.latencyMs ?? trace.latency_ms,
            violations: newViolations,
          };
        }));
      };

      const stillPending = (traces: Array<TraceDetailType | null>) => traces.some(
        (t) => (t?.scan_result?.reasons ?? []).some(
          (r) => r.evidence_status === "ANALYSIS_REQUIRED",
        ),
      );

      // Initial fetch at 3s for latency + evidence (if ready)
      await delay(3000);
      if (pollSessionRef.current !== sessionId) {
        setScanLoading(false);
        return;
      }
      const firstTraces = await fetchAllTraces();
      if (pollSessionRef.current !== sessionId) {
        setScanLoading(false);
        return;
      }
      mergeTraces(firstTraces);
      setScanLoading(false);

      // Continue polling if any violation evidence still being extracted
      if (stillPending(firstTraces)) {
        const MAX_ATTEMPTS = 6;
        const POLL_INTERVAL = 10000;
        for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await delay(POLL_INTERVAL);
          if (pollSessionRef.current !== sessionId) break;
          // eslint-disable-next-line no-await-in-loop
          const traces = await fetchAllTraces();
          if (pollSessionRef.current !== sessionId) break;
          mergeTraces(traces);
          if (!stillPending(traces)) break;
        }
      }
    } else {
      setScanLoading(false);
    }
  };

  const allViolations = scanGroups.flatMap((g) => g.violations);
  const hasAnyUnsafe = scanGroups.some((g) => g.decision === "UNSAFE");

  let totalChecks: number | null = null;
  if (scanLoaded) {
    const perScan = selectedPolicyIds.length > 0 ? selectedPolicyIds.length : policiesData.length;
    totalChecks = perScan * scanGroups.length;
  }

  return (
    <PanelProvider>
      <PageWrapper>
        <TopBar>
          <PageTitle>{t("pageTitle")}</PageTitle>
          <TokenGroup>
            <TokenLabel>{t("token.label")}</TokenLabel>
            <TokenInputWrapper>
              <TokenInput
                hasError={showServiceTokenFormatError || showServiceTokenLookupError}
                type={tokenVisible ? "text" : "password"}
                placeholder={t("token.placeholder")}
                value={serviceToken}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setServiceToken(e.target.value);
                  sessionStorage.setItem("sandbox_service_token", e.target.value);
                }}
              />
              <TokenToggleBtn type="button" aria-hidden="true" onClick={() => setTokenVisible((v) => !v)}>
                {tokenVisible
                  ? <Invisible className="invisible-icon" color="#fff" size="14" />
                  : <Visible className="visible-icon" color="#fff" size="14" />}
              </TokenToggleBtn>
            </TokenInputWrapper>
            {showServiceTokenFormatError && (
              <TokenErrorText>{t("token.errorFormat")}</TokenErrorText>
            )}
            {showServiceTokenLookupError && (
              <TokenErrorText>{t("token.errorLookup")}</TokenErrorText>
            )}
            {!showServiceTokenFormatError && hasValidServiceToken && isRefreshingServiceTokens && (
              <TokenInfoText>{t("token.verifying")}</TokenInfoText>
            )}
            {!showServiceTokenFormatError && !showServiceTokenLookupError && !isRefreshingServiceTokens && matchedServiceToken && (
              <TokenInfoText>{matchedServiceToken.service_token_name}</TokenInfoText>
            )}
          </TokenGroup>
        </TopBar>
        <PanelsRow>
          <LeftPanel>
            <HeaderRow>
              <HeaderTitle>{t("config.title")}</HeaderTitle>
              <ForceTriggerRow>
                <SaveTemplateLink
                  type="button"
                  onClick={() => setTemplateFormState({ mode: "saveCurrent" })}
                  disabled={!systemPrompt.trim() && !messages.some((m) => m.content.trim())}
                >
                  <Save size={14} color="var(--x-gray-400, #cfd0d3)" />
                  {t("config.saveAsTemplate")}
                </SaveTemplateLink>
                <Button
                  variant="secondary"
                  onClick={() => { setTemplateCategoryFilter("All"); setIsTemplateModalOpen(true); }}
                >
                  {t("config.attackScenario")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleRunAnalysis}
                  disabled={scanLoading || selectedPolicyIds.length === 0 || !messages.some((m) => m.role === "user" && m.content.trim())}
                >
                  {scanLoading ? t("config.scanning") : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Start size={14} color="currentColor" />
                      {t("config.runScan")}
                    </span>
                  )}
                </Button>
              </ForceTriggerRow>
            </HeaderRow>
            <ContentGrid>
              <LeftColumn>
                <ReactScrollbarsCustom>
                  <LeftColumnInner>
                    <SectionLabel>{t("systemPrompt.label")}</SectionLabel>
                    <TextField
                      as="textarea"
                      name="systemPrompt"
                      placeholder={t("systemPrompt.placeholder")}
                      value={systemPrompt}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSystemPrompt(e.target.value)}
                      style={{ minHeight: 160, resize: "vertical", lineHeight: 1.5 }}
                    />

                    <PoliciesHeader>
                      <PoliciesTitle>{t("policies.title")}</PoliciesTitle>
                      <CountBadge>{selectedPolicies.length}</CountBadge>
                    </PoliciesHeader>
                    {selectedPolicies.length > 0 && (
                      <PoliciesList>
                        {selectedPolicies.map((policy) => (
                          <PolicyRow key={policy.id}>
                            <PolicyTagWrap><RiskLevelTag level={policy.riskLevel} /></PolicyTagWrap>
                            <PolicyName>{policy.name}</PolicyName>
                          </PolicyRow>
                        ))}
                      </PoliciesList>
                    )}
                    <SelectPoliciesButton type="button" onClick={() => setIsPolicySelectorOpen(true)}>
                      <Edit size={14} color="var(--x-gray-200)" />
                      {t("policies.select")}
                    </SelectPoliciesButton>
                    {showPolicyError && (
                      <PolicyErrorText>{t("policies.errorRequired")}</PolicyErrorText>
                    )}
                  </LeftColumnInner>
                </ReactScrollbarsCustom>
              </LeftColumn>

              <RightColumn>
                <ReactScrollbarsCustom>
                  <RightColumnInner>
                    {scanLoaded && hasAnyUnsafe && (
                      <TestResultBanner variant="unsafe">
                        <ErrorFill size={20} color="var(--x-gray-200, #f6f6f7)" />
                        {t("result.alertUnsafe")}
                      </TestResultBanner>
                    )}
                    {scanLoaded && !hasAnyUnsafe && (
                      <TestResultBanner variant="safe">
                        <LatestVersion size={20} color="var(--x-gray-200, #f6f6f7)" />
                        {t("result.alertSafe")}
                      </TestResultBanner>
                    )}
                    {messages.map((msg, idx) => (
                      <MessageBlock key={msg.id}>
                        <MessageHeader>
                          {idx === 0 ? (
                            <div style={{ width: 240 }}>
                              <TextField
                                name="first-role"
                                value={t("messages.userInput")}
                                readOnly
                              />
                            </div>
                          ) : (
                            <div style={{ width: 240 }}>
                              <Select
                                isSearchable={false}
                                options={roleOptions}
                                value={roleOptions.find((o) => o.value === msg.role)}
                                onChange={(selected: unknown) => {
                                  const opt = selected as RoleOption | null;
                                  if (opt) updateMessage(msg.id, "role", opt.value);
                                }}
                              />
                            </div>
                          )}
                        </MessageHeader>
                        <TextField
                          as="textarea"
                          name={`message-${msg.id}`}
                          placeholder={msg.role === "user" ? t("messages.userPlaceholder") : t("messages.assistantPlaceholder")}
                          value={msg.content}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            updateMessage(msg.id, "content", e.target.value);
                          }}
                          style={{ minHeight: 155, resize: "vertical", lineHeight: 1.5 }}
                        />
                      </MessageBlock>
                    ))}
                    <AddMessageButton type="button" onClick={addMessage}>
                      {t("messages.addMessage")}
                    </AddMessageButton>
                  </RightColumnInner>
                </ReactScrollbarsCustom>
              </RightColumn>
            </ContentGrid>
          </LeftPanel>

          <AnalysisPanel>
            <ReactScrollbarsCustom>
              <AnalysisPanelInner>
                <AnalysisHeader>
                  <AnalysisIcon><Defend size={18} color="var(--x-gray-200, #f6f6f7)" /></AnalysisIcon>
                  <AnalysisTitle>{t("analysis.title")}</AnalysisTitle>
                </AnalysisHeader>
                <StatsRow>
                  <StatBox>
                    <StatValue>{totalChecks ?? "—"}</StatValue>
                    <StatLabel>{t("analysis.totalChecks")}</StatLabel>
                  </StatBox>
                  <StatBox>
                    <StatValue color="var(--x-red, #ff595a)">
                      {scanLoaded ? allViolations.length : "—"}
                    </StatValue>
                    <StatLabel>{t("analysis.violations")}</StatLabel>
                  </StatBox>
                </StatsRow>
                {scanError && (
                  <ErrorText>
                    <ErrorIcon>
                      <ErrorFill size={18} color="var(--x-red, #ff595a)" />
                    </ErrorIcon>
                    <ErrorContent>
                      <ErrorMessage>{scanError}</ErrorMessage>
                      {scanErrorAt && <ErrorTimestamp>{scanErrorAt}</ErrorTimestamp>}
                    </ErrorContent>
                  </ErrorText>
                )}
                {scanGroups.map((group, gIdx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={gIdx}>
                    {gIdx > 0 && <Divider />}
                    <FieldRow>
                      <FieldLabel>{t("analysis.traceId")}</FieldLabel>
                      <FieldValue>{group.traceId || "—"}</FieldValue>
                    </FieldRow>
                    <FieldRow>
                      <FieldLabel>{t("analysis.latency")}</FieldLabel>
                      <FieldValue>
                        {group.latencyMs != null && `${group.latencyMs} ms`}
                        {group.latencyMs == null && scanLoading && (
                          <Spinner animation="border" size="sm" variant="primary" />
                        )}
                        {group.latencyMs == null && !scanLoading && "—"}
                      </FieldValue>
                    </FieldRow>
                    <FieldRow>
                      <FieldLabel>{t("analysis.scanType")}</FieldLabel>
                      <FieldValue>{group.scanType === "input" ? t("analysis.scanInput") : t("analysis.scanResponse")}</FieldValue>
                    </FieldRow>
                    <Divider />
                    <ViolationsTitle>{t("analysis.violationDetails")}</ViolationsTitle>
                    {group.violations.length === 0 && (
                      <ViolationsEmpty>{t("analysis.noViolations")}</ViolationsEmpty>
                    )}
                    {group.violations.map((v, vIdx) => {
                      const riskNum = policyRiskLevelByName.get(v.violated_policy_name)
                        ?? POLICY_RISK_LEVEL[v.violated_policy_name];
                      const evidenceItems = v.evidence ?? v.metadata ?? [];
                      const renderEvidence = () => {
                        if (evidenceItems.length > 0) {
                          return (
                            <EvidenceList>
                              {evidenceItems.map((e, eIdx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <EvidenceRow key={eIdx}>
                                  <EvidenceTypeBadge>{e.type}</EvidenceTypeBadge>
                                  <EvidenceText>{e.text}</EvidenceText>
                                </EvidenceRow>
                              ))}
                            </EvidenceList>
                          );
                        }
                        if (v.evidence_status === "UNAVAILABLE") {
                          return <EvidenceUnavailable>{t("analysis.evidenceUnavailable")}</EvidenceUnavailable>;
                        }
                        return (
                          <EvidencePending>
                            <SpinIcon />
                            {t("analysis.evidencePending")}
                          </EvidencePending>
                        );
                      };
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <ViolationCard key={vIdx}>
                          <ViolationHeaderRow>
                            {riskNum != null && <RiskLevelTag level={riskNum} />}
                            <ViolationPolicyName>
                              {POLICY_DISPLAY_NAMES[v.violated_policy_name] ?? v.violated_policy_name}
                            </ViolationPolicyName>
                          </ViolationHeaderRow>
                          <ViolationFieldLabel>{t("analysis.rationale")}</ViolationFieldLabel>
                          <ViolationFieldValue>{v.rationale}</ViolationFieldValue>

                          <ViolationFieldLabel>{t("analysis.evidence")}</ViolationFieldLabel>
                          {renderEvidence()}
                        </ViolationCard>
                      );
                    })}
                  </React.Fragment>
                ))}
                {!scanLoaded && !scanError && (
                  <ViolationsEmpty>{t("analysis.noScanYet")}</ViolationsEmpty>
                )}
              </AnalysisPanelInner>
            </ReactScrollbarsCustom>
          </AnalysisPanel>
        </PanelsRow>
      </PageWrapper>
      <PolicySelectorModal
        show={isPolicySelectorOpen}
        onClose={() => setIsPolicySelectorOpen(false)}
        policies={allPolicyOptions}
        selectedIds={selectedPolicyIds}
        onConfirm={(ids) => setSelectedPolicyIds(ids)}
      />
      <TemplateStyledModal
        title={t("templateModal.title")}
        show={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        showCloseBtn
        restoreFocus={false}
      >
        <Modal.Body>
          <AttackTemplatesTitle>{t("templateModal.subtitle")}</AttackTemplatesTitle>
          <div style={{ width: 300, marginBottom: 16 }}>
            <Select
              isSearchable={false}
              maxHeight="560px"
              components={{ MenuList: AttackScenarioMenuList }}
              options={[
                { value: "All", label: t("templateModal.filterAll") },
                ...mergedGroups
                  .filter((g) => g.category !== "Total Policies Select *")
                  .map((g) => {
                    const pk = CATEGORY_POLICY_MAP[g.category];
                    return { value: g.category, label: pk ? (POLICY_DISPLAY_NAMES[pk] ?? g.category) : g.category };
                  }),
                ...mergedGroups
                  .filter((g) => g.category === "Total Policies Select *")
                  .map((g) => ({ value: g.category, label: t("templateModal.totalPoliciesCategory") })),
              ]}
              value={{
                value: templateCategoryFilter,
                label: templateCategoryFilter === "All"
                  ? t("templateModal.filterAll")
                  : (() => {
                    if (templateCategoryFilter === "Total Policies Select *") return t("templateModal.totalPoliciesCategory");
                    const pk = CATEGORY_POLICY_MAP[templateCategoryFilter];
                    return pk ? (POLICY_DISPLAY_NAMES[pk] ?? templateCategoryFilter) : templateCategoryFilter;
                  })(),
              }}
              onChange={(selected: unknown) => {
                const opt = selected as { value: string } | null;
                if (opt) setTemplateCategoryFilter(opt.value);
              }}
            />
          </div>
          {mergedGroups
            .filter((group) => templateCategoryFilter === "All" || group.category === templateCategoryFilter)
            .map((group) => {
              const policyKey = CATEGORY_POLICY_MAP[group.category];
              const friendlyName = policyKey ? (POLICY_DISPLAY_NAMES[policyKey] ?? group.category) : group.category;
              return (
                <div key={group.category}>
                  <TemplateCategoryTitle>{friendlyName}</TemplateCategoryTitle>
                  {group.templates.map((template) => {
                    const isCustom = "id" in template;
                    const tplName = template.i18nKey ? t(`templates.${template.i18nKey}.name`, template.name) : template.name;
                    const resolved = resolveTemplateContent(template);
                    const userMsgs = resolved.messages.filter((m) => m.role === "user");
                    const assistantMsgs = resolved.messages.filter((m) => m.role === "assistant");
                    return (
                      <TemplateCard key={isCustom ? (template as CustomPromptTemplate).id : template.name}>
                        <TemplateCardHeader>
                          <TemplateFieldLabel>{tplName}</TemplateFieldLabel>
                          {isCustom && (
                            <TemplateCardActions>
                              <TemplateActionBtn
                                type="button"
                                onClick={() => {
                                  setIsTemplateModalOpen(false);
                                  setTemplateFormState({ mode: "edit", editId: (template as CustomPromptTemplate).id });
                                }}
                              >
                                <Edit size={14} color="var(--x-gray-400, #cfd0d3)" />
                              </TemplateActionBtn>
                              <TemplateActionBtn
                                type="button"
                                onClick={() => handleDeleteTemplate((template as CustomPromptTemplate).id)}
                              >
                                <Delete size={14} color="var(--x-gray-400, #cfd0d3)" />
                              </TemplateActionBtn>
                            </TemplateCardActions>
                          )}
                        </TemplateCardHeader>
                        {resolved.systemPrompt && (
                          <>
                            <TemplateFieldLabel>{t("templateModal.templateSystemPrompt")}</TemplateFieldLabel>
                            <TemplateFieldValue>{resolved.systemPrompt}</TemplateFieldValue>
                          </>
                        )}
                        {userMsgs.map((m, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <React.Fragment key={`user-${i}`}>
                            <TemplateFieldLabel>{t("templateModal.templateUserInput")}</TemplateFieldLabel>
                            <TemplateFieldValue>{m.content}</TemplateFieldValue>
                          </React.Fragment>
                        ))}
                        {assistantMsgs.map((m, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <React.Fragment key={`assistant-${i}`}>
                            <TemplateFieldLabel>{t("templateModal.templateAppResponse")}</TemplateFieldLabel>
                            <TemplateFieldValue>{m.content}</TemplateFieldValue>
                          </React.Fragment>
                        ))}
                        <ApplyTemplateBtn type="button" onClick={() => applyTemplate(template, group.category)}>
                          {t("templateModal.applyScenario")}
                        </ApplyTemplateBtn>
                      </TemplateCard>
                    );
                  })}
                </div>
              );
            })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsTemplateModalOpen(false)}>
            {t("templateModal.close")}
          </Button>
        </Modal.Footer>
      </TemplateStyledModal>
      <TemplateFormModal
        show={templateFormState !== null}
        onClose={() => setTemplateFormState(null)}
        onSave={handleSaveTemplate}
        initialValues={getTemplateFormInitialValues()}
        title={getTemplateFormTitle()}
        extraCategories={customCategoryNames}
      />
    </PanelProvider>
  );
};

export default Sandbox;
